import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Gavel } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { COUNTRIES, countryByCode } from "../lib/countries";
import { useCurrency } from "../lib/currency";

export default function SignUp() {
  const { register } = useAuth();
  const nav = useNavigate();
  const { setCurrency } = useCurrency();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", seller_type: "individual", country: "IN" });
  const [busy, setBusy] = useState(false);
  const country = countryByCode(form.country);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const phone = form.phone.trim() ? `${country[2]} ${form.phone.trim().replace(/^0+/, "")}` : null;
      const u = await register({ ...form, phone, preferred_currency: country[3] });
      setCurrency(u.preferred_currency || country[3]);
      toast.success(`Welcome, ${u.name}!`);
      nav("/");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Please check your details (phone: digits only)");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[70vh] grid place-items-center px-4 py-16">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 mb-8">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-slate-900 text-amber-400"><Gavel className="h-5 w-5" /></span>
          <span className="font-extrabold text-xl">Auction<span className="text-amber-500">Bulls</span></span>
        </Link>
        <h1 className="text-3xl font-extrabold">Create your free account</h1>
        <p className="text-slate-500 mt-1">Bid, sell, and manage everything from one dashboard.</p>
        <form onSubmit={submit} className="mt-8 space-y-4">
          <div><Label>Full name</Label><Input data-testid="signup-name" required value={form.name} onChange={e => set("name", e.target.value)} /></div>
          <div><Label>Email</Label><Input data-testid="signup-email" required type="email" value={form.email} onChange={e => set("email", e.target.value)} /></div>
          <div><Label>Country</Label>
            <Select value={form.country} onValueChange={v => set("country", v)}>
              <SelectTrigger data-testid="signup-country"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">{COUNTRIES.map(c => <SelectItem key={c[0]} value={c[0]} data-testid={`signup-country-${c[0]}`}>{c[1]} ({c[2]})</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-[11px] text-slate-500 mt-1">Prices will be shown in {country[3]} as an estimate. Payments are charged in INR.</p>
          </div>
          <div><Label>Phone (optional)</Label>
            <div className="flex gap-2">
              <span className="inline-flex items-center px-3 rounded-md border border-slate-200 dark:border-slate-700 text-sm font-semibold" data-testid="signup-dial-code">{country[2]}</span>
              <Input data-testid="signup-phone" inputMode="tel" value={form.phone} onChange={e => set("phone", e.target.value.replace(/[^0-9 -]/g, ""))} />
            </div>
          </div>
          <div><Label>Password</Label><Input data-testid="signup-password" required type="password" value={form.password} onChange={e => set("password", e.target.value)} /></div>
          <Button data-testid="signup-submit" type="submit" disabled={busy} className="w-full bg-slate-900 hover:bg-slate-800 text-white h-11">{busy ? "Creating..." : "Create Account"}</Button>
        </form>
        <div className="mt-6 text-sm text-slate-500">
          Already have an account? <Link className="font-semibold text-slate-900 dark:text-white" to="/signin">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
